import {
  Box,
  Paper,
  Radio,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

export type ConfirmationMode = 'auto_lock' | 'pending_ack';

interface ModeOption {
  mode: ConfirmationMode;
  title: string;
  description: string;
}

const MODE_OPTIONS: ModeOption[] = [
  {
    mode: 'auto_lock',
    title: 'Book instantly',
    description:
      'Requests go straight onto the calendar as booked appointments — no review needed.',
  },
  {
    mode: 'pending_ack',
    title: 'Review requests first',
    description:
      'Requests hold the slot until someone on your team accepts or declines them.',
  },
];

export interface ConfirmationModeViewProps {
  mode: ConfirmationMode;
  isLoading?: boolean;
  disabled?: boolean;
  onModeChange: (mode: ConfirmationMode) => void;
}

/** Presentational picker for the practice's booking confirmation mode (PRD D1).
 * Three-option spectrum; selection saves immediately (select pattern). */
export function ConfirmationModeView({
  mode,
  isLoading = false,
  disabled = false,
  onModeChange,
}: ConfirmationModeViewProps) {
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          When a booking comes in
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose what happens once a booking lands on the calendar. Screening
          happens in the funnel above; this decides whether it still needs your
          sign-off.
        </Typography>
      </Box>

      {isLoading ? (
        <Stack spacing={1.5}>
          <Skeleton variant="rounded" height={72} />
          <Skeleton variant="rounded" height={72} />
          <Skeleton variant="rounded" height={72} />
        </Stack>
      ) : (
        <Stack spacing={1.5} role="radiogroup" aria-label="Booking confirmation mode">
          {MODE_OPTIONS.map(option => {
            const selected = option.mode === mode;
            return (
              <Paper
                key={option.mode}
                variant="outlined"
                onClick={() => {
                  if (!disabled && !selected) {
                    onModeChange(option.mode);
                  }
                }}
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: disabled ? 'default' : 'pointer',
                  borderColor: selected ? 'primary.main' : 'divider',
                  bgcolor: selected ? 'action.selected' : 'background.paper',
                  opacity: disabled && !selected ? 0.6 : 1,
                }}
              >
                <Radio
                  checked={selected}
                  disabled={disabled}
                  slotProps={{ input: { 'aria-label': option.title } }}
                  sx={{ p: 0.5 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {option.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                </Box>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}
